import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import useMinimumLoading from '@/hooks/useMinimumLoading';
import apiService from '@/services/apiService';
import { sortProvidersByRate } from '@/services/utils';
import type { OccupationalHealthServiceQuote } from '@/types/app';
import Provider from '../cards/Provider';
import Skeleton from '../cards/Skeleton';
import Carousel from './Carousel';

const SELECTION_TRANSITION_MS = 300;
const APPLICATION_FIELD = 'selected_occupational_health_service_quote_id' as const;

interface OccupationalHealthServiceProps {
	id: string;
	isSaving?: boolean;
	setIsSaving?: (saving: boolean) => void;
	onConfirm?: () => void;
	onBack?: () => void;
	onLoadingChange?: (isLoading: boolean) => void;
	onItemCountChange?: (itemCount: number) => void;
	showNavigation?: boolean;
	desktopVisibleCards?: number;
}

const OccupationalHealthService = ({
	id,
	isSaving = false,
	setIsSaving = () => {},
	onConfirm = () => {},
	onBack = () => {},
	onLoadingChange = () => {},
	onItemCountChange = () => {},
	showNavigation = true,
	desktopVisibleCards
}: OccupationalHealthServiceProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { applicationData, applicationManagement } = useAppContext();
	const [occupationalHealthProviders, setOccupationalHealthProviders] = useState<
		OccupationalHealthServiceQuote[]
	>([]);
	const [transitioningQuoteId, setTransitioningQuoteId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [fetchError, setFetchError] = useState<boolean>(false);

	const fetchOccupationalHealthProviders = useCallback(async () => {
		if (!id) return;
		setIsLoading(true);
		setFetchError(false);
		try {
			const providerQuotes = await apiService.quote.occupational_health_service.fetch(id);
			setOccupationalHealthProviders(sortProvidersByRate(providerQuotes));
		} catch (err) {
			console.error('Error fetching occupational_health_service quotes:', err);
			setFetchError(true);
		} finally {
			setIsLoading(false);
		}
	}, [id]);

	const fetchedIdRef = useRef<string | null>(null);
	useEffect(() => {
		// Guard against a duplicate GET on remount (React StrictMode double-invoke,
		// fast route re-entry); refetch only when the form id actually changes.
		if (fetchedIdRef.current === id) return;
		fetchedIdRef.current = id;
		fetchOccupationalHealthProviders();
	}, [id, fetchOccupationalHealthProviders]);

	const handleProviderToggle = useCallback(
		async (quoteId: string): Promise<void> => {
			if (isSaving) return;

			const previousSelected = applicationData[APPLICATION_FIELD];
			const isDeselecting = previousSelected === quoteId;

			applicationManagement.update({
				[APPLICATION_FIELD]: isDeselecting ? null : quoteId
			});

			setTransitioningQuoteId(quoteId);
			try {
				await applicationManagement.save(id, {
					...applicationData,
					[APPLICATION_FIELD]: isDeselecting ? null : quoteId
				});
				setTimeout(() => setTransitioningQuoteId(null), SELECTION_TRANSITION_MS);
			} catch (err) {
				console.error('Error saving occupational_health_service selection:', err);
				applicationManagement.update({
					[APPLICATION_FIELD]: previousSelected
				});
				setTransitioningQuoteId(null);
			}
		},
		[applicationData, applicationManagement, id, isSaving]
	);

	const handleSkipToggle = useCallback(async (): Promise<void> => {
		if (isSaving || applicationData[APPLICATION_FIELD] === null) return;
		const previousSelected = applicationData[APPLICATION_FIELD];
		applicationManagement.update({ [APPLICATION_FIELD]: null });
		try {
			await applicationManagement.save(id, {
				...applicationData,
				[APPLICATION_FIELD]: null
			});
		} catch (err) {
			console.error('Error clearing occupational_health_service selection:', err);
			applicationManagement.update({
				[APPLICATION_FIELD]: previousSelected
			});
		}
	}, [applicationData, applicationManagement, id, isSaving]);

	const handleConfirmOrSkip = useCallback(async (): Promise<void> => {
		const hasSelection = !!applicationData[APPLICATION_FIELD];

		if (!hasSelection) {
			applicationManagement.update({
				arboService: false,
				[APPLICATION_FIELD]: null
			});
			setIsSaving(true);
			try {
				await applicationManagement.save(id, {
					...applicationData,
					arboService: false,
					[APPLICATION_FIELD]: null
				});
				onConfirm();
			} catch (err) {
				console.error('Error saving skip occupational_health_service:', err);
			} finally {
				setIsSaving(false);
			}
		} else {
			applicationManagement.update({ arboService: true });
			setIsSaving(true);
			try {
				await applicationManagement.save(id, {
					...applicationData,
					arboService: true
				});
				onConfirm();
			} catch (err) {
				console.error('Error saving confirm occupational_health_service:', err);
			} finally {
				setIsSaving(false);
			}
		}
	}, [applicationData, applicationManagement, id, onConfirm, setIsSaving]);

	const isCardBusy = transitioningQuoteId !== null || isSaving;
	const selectedProviderId = applicationData[APPLICATION_FIELD];
	const isLoadingDisplayed = useMinimumLoading(isLoading);
	const isSectionLoading = isLoading || isLoadingDisplayed;

	useEffect(() => {
		onLoadingChange(isSectionLoading);
	}, [isSectionLoading, onLoadingChange]);

	useEffect(() => {
		return () => onLoadingChange(false);
	}, [onLoadingChange]);

	useEffect(() => {
		onItemCountChange(occupationalHealthProviders.length);
	}, [occupationalHealthProviders.length, onItemCountChange]);

	useEffect(() => {
		return () => onItemCountChange(0);
	}, [onItemCountChange]);

	return (
		<Carousel
			title={t('application.steps.step2.title')}
			subtitle={t('application.steps.step2.subtitle')}
			carouselKeyPrefix="occupational-health-service"
			items={occupationalHealthProviders}
			expectedNumQuotes={null}
			getItemId={(provider) => provider.quote_id}
			renderItem={(provider) => (
				<Provider
					provider={provider}
					isSelected={selectedProviderId === provider.quote_id}
					isDisabled={isCardBusy}
					isTransitioning={transitioningQuoteId === provider.quote_id}
					onSelect={handleProviderToggle}
				/>
			)}
			selectedId={selectedProviderId}
			isBusy={isCardBusy}
			isSaving={isSaving}
			isLoading={isLoading}
			isLoadingDisplayed={isLoadingDisplayed}
			error={fetchError}
			productName={t('productPackage.products.occupationalHealthService')}
			fetchErrorLabel={t('application.steps.step2.fetchError')}
			noItemsLabel={t('application.steps.step2.noProvidersAvailable')}
			confirmLabel={t('application.steps.step2.confirmButton')}
			skipLabel={t('application.steps.step2.skipButton')}
			onRetry={fetchOccupationalHealthProviders}
			onSkip={handleSkipToggle}
			onConfirm={handleConfirmOrSkip}
			onBack={onBack}
			showNavigation={showNavigation}
			placeholderLimits={{ base: 0, md: 1, lg: 3 }}
			desktopVisibleCards={desktopVisibleCards}
			renderSkeleton={() => <Skeleton showPackageName footerLinks="single" />}
		/>
	);
};

export default OccupationalHealthService;
